import { useCallback } from 'react';

import { format, startOfDay } from 'date-fns';
import merge from 'lodash/merge';

import { dateHelpers } from '@nocowanie/core/helpers';
import { useSearchCookie } from '@nocowanie/common-ui/hooks';

import { listingStateSlice, useAppDispatch, useAppSelector } from '@app/app-state';
import { environment } from '@app/environments/environment';
import { buildListingFilterCountArgs } from '@app/helpers/listing-counters.helpers';
import { isPrettyUrl } from '@app/helpers/search-params.helpers';
import { useSearch } from '@app/hooks/useSearch';
import { defaultSearchCriteria, SearchFilters } from '@app/models';
import { fetchCampaignListingMeta, fetchListingItems } from '@app/state/api-thunks/listing.thunk';

/**
 * Hook to access and update listing search state
 */
export function useListingSearch() {
    const dispatch = useAppDispatch();
    const cmsId = useAppSelector(listingStateSlice.selectors.selectListingCmsId);
    const cmsListingMeta = useAppSelector(listingStateSlice.selectors.selectListingMeta);
    const { searchCriteria, campaignListingId, filters, currentPage, ...rest } = useSearch();

    const fetchCampaignMeta = useCallback(() => {
        if (!campaignListingId) {
            return;
        }

        dispatch(
            fetchCampaignListingMeta({
                slug: campaignListingId,
                preview: false,
            }),
        );
    }, [campaignListingId]);

    const { getSearchCriteriaFromCookie } = useSearchCookie(environment.COOKIE_DOMAIN);

    const prepareFetchParams = useCallback(() => {
        const {
            checkIn: cmsCheckIn,
            checkOut: cmsCheckOut,
            flexDates: cmsFlexDates,
        } = cmsListingMeta.data;
        const cookieCriteria = getSearchCriteriaFromCookie();

        let effectiveSearchCriteria: typeof searchCriteria;

        // Priority: URL (searchCriteria from Redux) > CMS (if applies) > cookie
        if (searchCriteria.checkInDate && searchCriteria.checkOutDate) {
            // Priority 1: URL has dates - use searchCriteria as-is (preserves all URL params)
            effectiveSearchCriteria = searchCriteria;
        } else {
            // URL has no dates - check CMS, then cookie
            if (
                cmsCheckIn &&
                cmsCheckOut &&
                dateHelpers.isValidRange(
                    startOfDay(new Date(cmsCheckIn)),
                    startOfDay(new Date(cmsCheckOut)),
                )
            ) {
                // Priority 2: CMS dates
                effectiveSearchCriteria = {
                    ...searchCriteria,
                    checkInDate: cmsCheckIn,
                    checkOutDate: cmsCheckOut,
                    flexDates: cmsFlexDates ?? environment.DEFAULT_FLEX_DATES,
                };
            } else if (
                cookieCriteria?.dates?.checkInDate &&
                cookieCriteria?.dates?.checkOutDate &&
                dateHelpers.isValidRange(
                    cookieCriteria.dates.checkInDate,
                    cookieCriteria.dates.checkOutDate,
                )
            ) {
                // Priority 3: Cookie — user's previously selected criteria
                effectiveSearchCriteria = {
                    ...searchCriteria,
                    checkInDate: format(cookieCriteria.dates.checkInDate, 'yyyy-MM-dd'),
                    checkOutDate: format(cookieCriteria.dates.checkOutDate, 'yyyy-MM-dd'),
                    flexDates: cookieCriteria.flexDates ?? searchCriteria?.flexDates,
                };
            } else if (cmsId) {
                // Priority 4: Campaign listing with no cookie and no CMS dates
                effectiveSearchCriteria = {
                    ...searchCriteria,
                    checkInDate: null,
                    checkOutDate: null,
                };
            } else {
                // Non-campaign listing: use searchCriteria as-is (URL or default dates)
                effectiveSearchCriteria = searchCriteria;
            }
        }

        if (searchCriteria.guests.guestCount) {
            effectiveSearchCriteria = searchCriteria;
        } else {
            if (cookieCriteria?.guests) {
                const adultsCount =
                    cookieCriteria.guests.adultsCount ??
                    searchCriteria?.guests?.adultsCount ??
                    defaultSearchCriteria.guests.adultsCount;
                const childrenCount =
                    cookieCriteria.guests.childrenCount ??
                    searchCriteria?.guests?.childrenCount ??
                    defaultSearchCriteria.guests.childrenCount;
                effectiveSearchCriteria.guests = {
                    adultsCount,
                    childrenCount,
                    childrenAges:
                        cookieCriteria.guests.childrenAges ??
                        searchCriteria?.guests?.childrenAges ??
                        [],
                    guestCount: adultsCount + childrenCount,
                };
            }
        }

        return {
            searchCriteria: effectiveSearchCriteria,
            page: currentPage,
            campaignListingId: cmsId,
            prettyUrlListing:
                typeof window !== 'undefined' &&
                isPrettyUrl(new URLSearchParams(window.location.search)),
        };
    }, [cmsId, cmsListingMeta.data, currentPage, searchCriteria, getSearchCriteriaFromCookie]);

    const fetchListingItemsAction = useCallback(() => {
        const { searchCriteria, page, campaignListingId, prettyUrlListing } = prepareFetchParams();

        dispatch(
            fetchListingItems({
                searchCriteria,
                searchFilters: filters,
                page,
                campaignListingId,
                prettyUrlListing,
            }),
        );
    }, [filters, prepareFetchParams]);

    /** Arguments of the counter request for the current search with `filterOverride` applied */
    const getFilterCountArgs = useCallback(
        (filterOverride: Partial<SearchFilters>) => {
            const { searchCriteria, campaignListingId } = prepareFetchParams();

            return buildListingFilterCountArgs({
                searchCriteria,
                searchFilters: merge({}, filters, filterOverride),
                campaignListingId,
            });
        },
        [filters, prepareFetchParams],
    );

    return {
        currentPage,
        campaignListingId,
        searchCriteria,
        filters,
        fetchCampaignListingMeta: fetchCampaignMeta,
        fetchListingItems: fetchListingItemsAction,
        getFilterCountArgs,
        ...rest,
    };
}
